/**
 * ehr#478 (PROGRESS_NOTE_10-14-V81 Type, place and billing): the billing code a note bills (the
 * one the type and length make, or one picked by hand), interactive complexity (+90785), the place
 * of service, the billing checks (each with a one-click fix) and what billing still needs before
 * sign-off. Pure, so it's tested on its own. The payor's own rules stay in payorChecks (@ehr/shared).
 */
import { codesIn } from '@ehr/shared';
import { UNIT_CODES, unitsOf, type SignCheck } from './noteV81.js';
import { NO_CODE_KINDS } from './noteLayouts.js';

/** V81 COMPLEXITY_OK: 90785 interactive complexity goes with these codes only. */
export const COMPLEXITY_OK = ['90791', '90832', '90834', '90837', '90853'] as const;

/** V81 IC_REASONS (97 A): +90785 needs one of the four CMS reasons on the note. */
export const IC_REASONS = [
  'Maladaptive communication (high anxiety, reactivity, repeated questions or disagreement) that complicated care',
  'Caregiver emotions or behavior interfered with carrying out the treatment plan',
  'A sentinel event was disclosed and a mandated report was discussed with the client',
  'Play equipment, devices, an interpreter or a translator was needed to overcome a barrier to communication',
] as const;

/** V81 US_STATES: where a telehealth or phone client is. */
export const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
] as const;

/** V81 THERAPY_CPT: the codes a therapy note can pick. */
export const THERAPY_CPT: readonly [string, string][] = [
  ['90791', 'Diagnostic evaluation'],
  ['90832', 'Psychotherapy, 30 min'],
  ['90834', 'Psychotherapy, 45 min'],
  ['90837', 'Psychotherapy, 60 min'],
  ['90839', 'Crisis psychotherapy, first 60 min'],
  ['90846', 'Family therapy without client'],
  ['90847', 'Family therapy with client'],
  ['90853', 'Group psychotherapy'],
];
/** V81 DRAWER_CPT: the codes the other notes (case management, medication, testing, …) pick. */
export const OTHER_CPT: readonly [string, string][] = [
  ['90791', 'Diagnostic evaluation'],
  ['90846', 'Family therapy without client'],
  ['H0006', 'Case management'],
  ['T1016', 'Case management, each 15 min'],
  ['T1017', 'Targeted case management, each 15 min'],
  ['H0038', 'Peer support, each 15 min'],
  ['H2014', 'Skills training, each 15 min'],
  ['H2017', 'Psychosocial rehabilitation, each 15 min'],
  ['99212', 'Medication management, E/M level 2'],
  ['99213', 'Medication management, E/M level 3'],
  ['99214', 'Medication management, E/M level 4'],
  ['96130', 'Psych testing evaluation, first hour'],
  ['96136', 'Test administration, first 30 min'],
];
const ALL_CPT = [...THERAPY_CPT, ...OTHER_CPT];
/** "90834 · Psychotherapy, 45 min", or the code alone when it isn't on the lists. */
export function cptLabel(code: string): string {
  const x = ALL_CPT.find((c) => c[0] === code);
  return x ? `${x[0]} · ${x[1]}` : code;
}

/** CPT time ranges (V81 TIME_RANGES). */
const TIME_RANGES: Record<string, [number, number]> = {
  '90832': [16, 37],
  '90834': [38, 52],
  '90837': [53, Infinity],
  '90839': [30, Infinity],
  '90846': [26, Infinity],
  '90847': [26, Infinity],
};
/** Codes that go with a kind of note (V81 TYPE_CODES): the kind decides the code. */
const TYPE_CODES: Record<string, string[]> = {
  'New Client': ['90791'],
  Family: ['90847', '90846'],
  Couples: ['90847', '90846'],
  Group: ['90853'],
  'Collateral contact': ['90846'],
};
const rangeText = (r: [number, number]) =>
  r[1] === Infinity ? `${r[0]} min or more` : `${r[0]} to ${r[1]} min`;
const complexityOk = (code: string) => (COMPLEXITY_OK as readonly string[]).includes(code);

/** The note's base code: the one picked by hand ('' is "No code"), else the automatic one's first. */
export function baseCode(auto: string, pick: string | undefined): string {
  return pick !== undefined ? pick : (codesIn(auto)[0] ?? '');
}

/**
 * The code as the note bills it (V81 codeText): the automatic code as it is ("90839 + 90840",
 * "H0038 x2"), or a picked code with its crisis add-ons and units; + 90785 when interactive
 * complexity is ticked on a code it goes with.
 */
export function noteCode(x: {
  auto: string;
  pick: string | undefined;
  minutes: number | null;
  complexity: boolean;
}): string {
  let out = x.auto;
  if (x.pick !== undefined) {
    const c = x.pick;
    out = c;
    if (c === '90839' && x.minutes != null && x.minutes >= 75) {
      const n = 1 + Math.floor((x.minutes - 75) / 30);
      out = `90839 + 90840${n > 1 ? ` x${n}` : ''}`;
    }
    const u = unitsOf(c, x.minutes);
    if (u && u > 1) out = `${c} x${u}`;
  }
  const base = baseCode(x.auto, x.pick);
  return out && x.complexity && complexityOk(base) ? `${out} + 90785` : out;
}

/** "2 units (30 min)" for a timed code, else ''. */
export function unitsText(code: string, minutes: number | null): string {
  const n = unitsOf(code, minutes);
  return n == null ? '' : `${n} unit${n === 1 ? '' : 's'} (${minutes} min)`;
}

/** Place of service (V81 billingLine): 11 in person; telehealth 10 at home, 02 anywhere else. */
export function posOf(place: string, clientAt: string | undefined): string {
  if (place === 'In person') return '11 Office';
  return clientAt === 'Other' ? '02 Telehealth other' : '10 Telehealth in home';
}

export interface BillCheck {
  text: string;
  /** The code a click switches to; 'none' clears the code; '' has no fix. */
  fix: string;
}
/**
 * The code as set up, checked (V81 billingChecks, without the payor's rules): a code on a kind that
 * isn't billed, the wrong code for the kind, the length outside the code's time range, 90785 on a
 * code it doesn't go with, and the units of a timed code.
 */
export function codeChecks(x: {
  kind: string;
  base: string;
  /** The code the type and length make (the fix for a code that doesn't fit). */
  autoBase: string;
  minutes: number | null;
  complexity: boolean;
}): BillCheck[] {
  const out: BillCheck[] = [];
  const c = x.base;
  const d = x.minutes;
  if (!c) return out;
  if ((NO_CODE_KINDS as readonly string[]).includes(x.kind))
    out.push({ text: `${x.kind} isn't billed with a code (${c} is set).`, fix: 'none' });
  else if (TYPE_CODES[x.kind] && !TYPE_CODES[x.kind]!.includes(c)) {
    const ok = TYPE_CODES[x.kind]!;
    return [{ text: `${x.kind} sessions bill ${ok.join(' or ')}, not ${c}.`, fix: ok[0]! }];
  }
  const r = TIME_RANGES[c];
  if (r && d != null && d > 0 && (d < r[0] || d > r[1])) {
    const fit = x.autoBase && x.autoBase !== c ? x.autoBase : '';
    out.push({
      text: `${d} min doesn't fit ${c} (${rangeText(r)}).${fit ? ` ${fit} fits.` : ''}`,
      fix: fit,
    });
  }
  if (x.complexity && !complexityOk(c))
    out.push({ text: `90785 can't be billed with ${c}.`, fix: '' });
  const u = UNIT_CODES[c];
  if (u && d != null && d > 0) {
    const n = unitsOf(c, d) ?? 0;
    if (n === 0)
      out.push({
        text: `${d} min is under the minimum for ${c} (${Math.ceil(u / 2)} min for 1 unit).`,
        fix: '',
      });
    else if (d % u)
      out.push({
        text: `${d} min bills as ${n} unit${n === 1 ? '' : 's'} of ${c}; whole units are ${u} min each.`,
        fix: '',
      });
  }
  return out;
}

/**
 * What billing still needs before sign-off (V81 signoffMissingItems, the billing part): a code
 * (unless the note isn't billed), the reason for +90785, a timed code's minimum, and the client's
 * state for telehealth and phone. They go to Type, place and billing.
 */
export function billingSignChecks(x: {
  /** The note is billed (not a No billing kind, a family member, or someone not present). */
  billed: boolean;
  base: string;
  minutes: number | null;
  complexity: boolean;
  icReason: string;
  place: string;
  locState: string;
}): SignCheck[] {
  const out: SignCheck[] = [];
  if (x.billed && !x.base) out.push({ label: 'Billing code', target: 'billing' });
  if (x.complexity && x.base && complexityOk(x.base) && !x.icReason.trim())
    out.push({ label: 'Interactive complexity reason', target: 'billing' });
  const u = UNIT_CODES[x.base];
  if (u && x.minutes != null && x.minutes > 0 && unitsOf(x.base, x.minutes) === 0)
    out.push({ label: `${x.base} needs at least ${Math.ceil(u / 2)} min`, target: 'session' });
  if (x.place !== 'In person' && !x.locState.trim())
    out.push({ label: 'Client location (state) for telehealth', target: 'billing' });
  return out;
}
